import { useEffect, useRef, useState } from 'react';

import { buildUniformFilterObject } from '../../../filterable-gallery/src/utils/filterEngine';
import {
	filtersToUniformFilterObject,
	readStateFromUrl,
	uniformFilterObjectToFlat,
	writeStateToUrl,
} from '../../../filterable-gallery/src/utils/urlSync';

import type { UniformFilterObject } from '../../../filterable-gallery/src/utils/types';
import type { CareersFilterState, UseCareersFilterStateOptions } from '../types';

/**
 * State machine for `careersGallery`'s checkboxes + search. Copied and
 * simplified from `filterable-gallery/src/hooks/use-filter-state.ts`: no
 * dropdown, no staged phase — toggling a checkbox commits immediately.
 * `stagedActive` stays on `UniformFilterObject` only for shape compatibility
 * with the imported engine/urlSync functions; here it is always kept equal
 * to `committedActive`.
 *
 * Known limitation inherited from reusing `urlSync.ts` unmodified: its
 * `term`/filters mutual exclusion means committing a non-empty search query
 * drops facet params from the URL (though not from in-memory state), so a
 * reload while a search term is active will not rehydrate prior facet
 * selections. The reference UI shows `team`/`location`/`search` as
 * independent URL params — fixing this would mean editing the shared
 * `urlSync.ts`, which is out of this component's scope.
 */
export function useCareersFilterState({
	items,
	filterDefs,
	urlSync,
	optionsDataSources,
}: UseCareersFilterStateOptions): CareersFilterState {
	const [filterObject, setFilterObject] = useState<UniformFilterObject>(() =>
		buildUniformFilterObject(items, filterDefs, optionsDataSources),
	);
	const [searchQuery, setSearchQueryState] = useState('');

	// Rebuild only when the item set actually changes shape, not on every
	// render — Uniform recreates `items`/`filterDefs` object identities
	// without the underlying data changing. Item COUNT is the change proxy
	// (same trade-off as use-filter-state.ts), merging forward existing
	// selections for options that still exist.
	const itemCount = items.length;
	useEffect(() => {
		const fresh = buildUniformFilterObject(items, filterDefs, optionsDataSources);
		setFilterObject((prev) => {
			const merged: UniformFilterObject = Object.fromEntries(
				Object.entries(fresh).map(([filterId, subFilter]) => [
					filterId,
					Object.fromEntries(
						Object.entries(subFilter).map(([optionId, option]) => [
							optionId,
							prev[filterId]?.[optionId] ?? option,
						]),
					),
				]),
			);
			return isFilterObjectEqual(merged, prev) ? prev : merged;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [itemCount, filterDefs, optionsDataSources]);

	// Writing query params breaks the Uniform preview iframe — disable URL
	// sync there. Computed once via ref since preview state never changes
	// mid-session.
	const isUniformPreviewRef = useRef(
		typeof window !== 'undefined' && window.location.pathname.includes('/uniform-preview'),
	);
	const effectiveUrlSync = urlSync && !isUniformPreviewRef.current;

	// Hydrate from URL on mount + browser back/forward.
	useEffect(() => {
		if (!effectiveUrlSync || typeof window === 'undefined') return;

		const hydrate = () => {
			const urlState = readStateFromUrl(window.location.search, filterDefs);
			setFilterObject((prev) => filtersToUniformFilterObject(urlState.filters, prev));
			setSearchQueryState(urlState.query);
		};

		hydrate();
		window.addEventListener('popstate', hydrate);
		return () => window.removeEventListener('popstate', hydrate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveUrlSync, filterDefs]);

	const pushUrl = (nextFilterObject: UniformFilterObject, nextQuery: string) => {
		if (!effectiveUrlSync) return;
		writeStateToUrl({
			filters: uniformFilterObjectToFlat(nextFilterObject),
			query: nextQuery,
			sortId: undefined,
		});
	};

	const toggleOption = (facetId: string, optionValue: string) => {
		setFilterObject((prev) => {
			const subFilter = prev[facetId] ?? {};
			const nextActive = !subFilter[optionValue]?.committedActive;
			const next: UniformFilterObject = {
				...prev,
				[facetId]: {
					...subFilter,
					[optionValue]: { committedActive: nextActive, stagedActive: nextActive },
				},
			};
			pushUrl(next, searchQuery);
			return next;
		});
	};

	const clearAllFilters = () => {
		setFilterObject((prev) => {
			const cleared: UniformFilterObject = Object.fromEntries(
				Object.entries(prev).map(([filterId, subFilter]) => [
					filterId,
					Object.fromEntries(
						Object.keys(subFilter ?? {}).map((optionId) => [
							optionId,
							{ committedActive: false, stagedActive: false },
						]),
					),
				]),
			);
			pushUrl(cleared, searchQuery);
			return cleared;
		});
	};

	const setSearchQuery = (query: string) => setSearchQueryState(query);

	const commitSearchQuery = () => pushUrl(filterObject, searchQuery);

	return {
		filterObject,
		searchQuery,
		toggleOption,
		setSearchQuery,
		commitSearchQuery,
		clearAllFilters,
	};
}

function isFilterObjectEqual(a: UniformFilterObject, b: UniformFilterObject): boolean {
	const filterIds = Object.keys(a);
	if (filterIds.length !== Object.keys(b).length) return false;

	return filterIds.every((filterId) => {
		const aSubFilter = a[filterId] ?? {};
		const bSubFilter = b[filterId] ?? {};
		const optionIds = Object.keys(aSubFilter);
		if (optionIds.length !== Object.keys(bSubFilter).length) return false;

		return optionIds.every(
			(optionId) => aSubFilter[optionId]?.committedActive === bSubFilter[optionId]?.committedActive,
		);
	});
}
